/**
 * The ONLY place in the front that knows the icon library (today: Lucide).
 * To switch libraries: change only this file (and <Icon>), keeping the semantic NAMES.
 * ESLint forbids importing 'lucide-react' outside packages/ui.
 *
 * New icon: add it here with a name based on its MEANING (not its drawing).
 */
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Award,
  Bell,
  Book,
  Bookmark,
  BookmarkCheck,
  Calendar,
  Camera,
  Check,
  Clock,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleAlert,
  SquareCheckBig,
  ClipboardCheck,
  DollarSign,
  MapPin,
  Pencil,
  Lock,
  Monitor,
  CircleCheck,
  CircleMinus,
  Copy,
  CreditCard,
  Download,
  Ellipsis,
  ExternalLink,
  Eye,
  EyeOff,
  File,
  LayoutGrid,
  Inbox,
  Info,
  LoaderCircle,
  LogOut,
  Mail,
  Menu,
  MonitorSmartphone,
  PanelLeftClose,
  PanelLeftOpen,
  QrCode,
  RotateCw,
  Search,
  Settings,
  Layers,
  ListFilter,
  Moon,
  Sun,
  Share2,
  Smartphone,
  User,
  WifiOff,
  X,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ThumbsDown,
  ThumbsUp,
  Upload,
  Paperclip,
  createLucideIcon,
  type LucideIcon,
} from 'lucide-react'

// Drawings of the prototype (one-home.html) with no Lucide equivalent: same 24×24 grid, stroke 2.
const EventsCalendar = createLucideIcon('events-calendar', [
  ['rect', { x: '3', y: '4', width: '18', height: '18', rx: '2', key: 'r' }],
  ['path', { d: 'M16 2v4M8 2v4M3 10h18', key: 'l' }],
  ['circle', { cx: '12', cy: '16', r: '2', key: 'c' }],
])
const GraduationCapProto = createLucideIcon('graduation-cap-proto', [
  ['path', { d: 'M22 10v6M2 10l10-5 10 5-10 5z', key: 'a' }],
  ['path', { d: 'M6 12v5c3 3 9 3 12 0v-5', key: 'b' }],
])
const BriefcaseProto = createLucideIcon('briefcase-proto', [
  ['rect', { x: '2', y: '7', width: '20', height: '14', rx: '2', key: 'a' }],
  ['path', { d: 'M16 7V5a2 2 0 0 0-4 0v2', key: 'b' }],
])
const Gradebook = createLucideIcon('gradebook', [
  ['path', { d: 'M4 4h16v16H4z', key: 'a' }],
  ['path', { d: 'M8 8h8M8 12h8M8 16h5', key: 'b' }],
])
const AppsDots = createLucideIcon('apps-dots', [
  ['circle', { cx: '8', cy: '8', r: '2', key: 'a' }],
  ['circle', { cx: '16', cy: '8', r: '2', key: 'b' }],
  ['circle', { cx: '8', cy: '16', r: '2', key: 'c' }],
  ['circle', { cx: '16', cy: '16', r: '2', key: 'd' }],
])

// Drawings follow the prototype (one-home.html sidebar), 20/10/2026.
export const ICONS = {
  home: LayoutGrid,
  courses: GraduationCapProto,
  calendar: Calendar,
  document: File,
  finance: DollarSign,
  card: CreditCard,
  career: BriefcaseProto,
  achievement: Award,
  notification: Bell,
  user: User,
  /** campo que o aluno não altera (dado da secretaria) */
  locked: Lock,
  /** trocar a foto */
  photo: Camera,
  /** editar uma seção ou um dado */
  edit: Pencil,
  settings: Settings,
  /* No menu do usuário o ícone mostra o tema ATUAL e o texto diz o que o clique faz,
     como no protótipo (sol + "Modo escuro"). */
  themeLight: Sun,
  themeDark: Moon,
  logout: LogOut,
  email: Mail,
  phone: Smartphone,
  menu: Menu,
  search: Search,
  close: X,
  confirm: Check,
  success: CircleCheck,
  error: CircleAlert,
  warning: AlertTriangle,
  info: Info,
  neutral: CircleMinus,
  copy: Copy,
  download: Download,
  share: Share2,
  externalLink: ExternalLink,
  qrCode: QrCode,
  show: Eye,
  hide: EyeOff,
  back: ArrowLeft,
  forward: ArrowRight,
  previous: ChevronLeft,
  next: ChevronRight,
  expand: ChevronDown,
  collapse: ChevronUp,
  more: Ellipsis,
  sidebarCollapse: PanelLeftClose,
  sidebarExpand: PanelLeftOpen,
  retry: RotateCw,
  sortable: ArrowUpDown,
  sortAscending: ArrowUp,
  sortDescending: ArrowDown,
  empty: Inbox,
  offline: WifiOff,
  survey: Search,
  report: SquareCheckBig,
  checklist: ClipboardCheck,
  library: Book,
  devices: MonitorSmartphone,
  loading: LoaderCircle,
  like: ThumbsUp,
  dislike: ThumbsDown,
  upload: Upload,
  attachment: Paperclip,
  portal: Monitor,
  gradebook: Gradebook,
  events: EventsCalendar,
  /** evento principal que agrupa atividades (`IC.layers` do protótipo do Eventos) */
  eventGroup: Layers,
  campus: MapPin,
  apps: AppsDots,
  time: Clock,
  location: MapPin,
  favorite: Bookmark,
  favorited: BookmarkCheck,
  /* três linhas decrescentes, como o SVG do `.filter-head-title` do protótipo do
     Eventos — não os sliders com botões */
  filter: ListFilter,
} satisfies Record<string, LucideIcon>

export type IconName = keyof typeof ICONS
